import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { bindActionCreators } from 'redux'
import { connect } from 'react-redux'
import { translate } from 'react-i18next'

import * as stockTransferReportActions from 'action/stockTransferReportNew'
import * as reportActions from 'action/report'
import CommonReport from 'component/commonReport/CommonReport'

import columns from 'component/stockTransferNew/columns'
import moment from 'moment'
import { formatColumnsByType } from 'utils/formatColumnsByType'
import { DESTINY, ORIGIN } from 'config/stockTypes'
import StockTransferStatus from 'constants/StockTransferStatus'

const STOCK_TRANSFER_CSV = 18

const DEFAULT_PAGINATION = {
  hasPagination: true,
  page: 1,
  size: 10,
}

const DEFAULT_INITIAL_VALUES = {
  branchIdList: [],
  cdLm: '',
  destinyList: [],
  dtEnd: null,
  dtStart: null,
  originList: [],
  sectionIdList: [],
  status:[]
}

const getRestoredFiltersFromLocation = (location) => {
  const query = (location && location.query) || {}
  const dtStart = query.stDtStart
  const dtEnd = query.stDtEnd

  if (!dtStart || !dtEnd) return null

  const mStart = moment(dtStart, 'DD/MM/YYYY', true)
  const mEnd = moment(dtEnd, 'DD/MM/YYYY', true)
  if (!mStart.isValid() || !mEnd.isValid()) return null

  const parseCsv = (value) =>
    value
      ? String(value)
        .split(',')
        .map((item) => item.trim())
        .filter(Boolean)
      : []

  const branchIdList = parseCsv(query.stBranchIdList)
  const sectionIdList = parseCsv(query.stSectionIdList)
    .map((value) => Number(value))
    .filter((value) => !Number.isNaN(value))
  const originList = parseCsv(query.stOriginList)
  const destinyList = parseCsv(query.stDestinyList)
  const status = parseCsv(query.stStatus)
    .map((value) => Number(value))
    .filter((value) => !Number.isNaN(value))

  return {
    branchIdList,
    sectionIdList,
    originList: originList[0] || '',
    destinyList: destinyList[0] || '',
    cdLm: query.stCdLm ? String(query.stCdLm) : '',
    status,
    dtRange: [mStart.format(), mEnd.format()],
  }
}

const StockTransferReportContainer = ({
  auth,
  fetch,
  translate,
  fetchFluxReport,
  location,

}) => {
  const { user } = auth || {}; 
  const dsToken = user?.dsToken;
  const restoredFilters = getRestoredFiltersFromLocation(location)

  const [filters, setFilters] = useState(DEFAULT_INITIAL_VALUES)
  const [lm, setLm] = useState(restoredFilters ? restoredFilters.cdLm : '')
  const getOption = (option) => {
    const [, stock] = option
    return { label: translate(stock.label), value: stock.value }
  }

  const [ids, setIds] = useState([]);

  const handleFetch = (
    {
      branchIdList,
      cdLm,
      destinyList,
      originList,
      sectionIdList,
      dtRange,
      page,
      orderDirection,
      orderField,
      status
    },
    setDataInTable
  ) => {
    const [dtStart, dtEnd] = dtRange

    const allStatusSelected = StockTransferStatus.allStatus().map(s => s.value).every(value => status.some(s => s.value === value));

     // If all status were selected, send an empty array
    const statusValues = allStatusSelected ? [] : (Array.isArray(status) ? status.map(s => s.value) : []);


    const params = {
      branchIdList,
      cdLm,
      destinyList: !destinyList.length ? [] : [destinyList],
      originList: !originList.length ? [] : [originList],
      dtStart: moment(dtStart).utc(true),
      dtEnd: moment(dtEnd).utc(true),
      sectionIdList,
      ...(orderDirection && { orderDirection }),
      ...(orderField && { orderField }),
      status: statusValues
    }

    setFilters(params)

    fetch(
      dsToken,
      {
        ...DEFAULT_PAGINATION,
        ...params,
        ...(page && { page }),
      },
      
      (data, total) => {
        const formatted = formatColumnsByType(columns, data, translate)

        const newIds = formatted.map((item) => item.codeProcess || '');
        setIds(newIds); 

        
    const transformedData = formatted.map((item, idx) => ({
        ...item,
        requestLog: item.codeProcess,
        dtTransfer: item.ftDate,
        dtApproval: data[idx].dtApproval, 
        statusName: translate(item.statusName) || item.statusName,
      }));

        setDataInTable({ total, data: transformedData, ids: newIds })
      }
    )
  }

  const exportCsv = () => {
    const abortController = new AbortController()
    const newFilters = {...filters}
    const inputFormat = "ddd MMM D YYYY HH:mm:ss [GMT]ZZ";
    const outputFormat = "YYYY-MM-DD HH:mm:ss.SSSSSS";
    const parsedDateStart = moment(newFilters.dtStart, inputFormat);
    const parsedDateEnd = moment(newFilters.dtEnd, inputFormat);
    const dtStart = parsedDateStart.format(outputFormat);
    const dtEnd = parsedDateEnd.format(outputFormat);

    newFilters.dtStart = dtStart
    newFilters.dtEnd = dtEnd
    fetchFluxReport(dsToken, { tpReport: STOCK_TRANSFER_CSV }, newFilters, translate, abortController)
    
  }

  const fields = [
    {
      type: 'branchs',
      name: 'branchIdList',
      label: translate('irc.store'),
      help_text: translate('irc.choose-store'),
      error_text: translate('field_must_be_filled'),
      required: true,
    },
    {
      type: 'sections',
      name: 'sectionIdList',
      label: translate('irc.section'),
      help_text: translate('irc.choose-section'),
      error_text: translate('field_must_be_filled'),
      required: lm.length === 0,
      onChangeValue: (_, setValueToField) => {
        setValueToField('cdLm', '')
        setLm('')
      },
    },
    {
      type: 'select',
      name: 'originList',
      label: translate('stock.transfer.origin.list'),
      help_text: translate('stock.select'),
      required: false,
      options: Object.entries(ORIGIN).map(getOption),
    },
    {
      type: 'select',
      name: 'destinyList',
      label: translate('stock.transfer.destiny.list'),
      help_text: translate('stock.select'),
      required: false,
      options: Object.entries(DESTINY).map(getOption),
    },
    {
      type: 'input',
      name: 'cdLm',
      label: translate('lm_label'),
      help_text: translate('mobile.hint.lm'),
      error_text: translate('field_must_be_filled'),
      required: false,
      onChangeValue: ({ target }, setValueToField) => {
        setLm(target.value)
        setValueToField('sections', [])
        setValueToField('sectionIdList', [])
      },
      attrs: {
        minLength: 8,
        maxLength: 8,
        mask: '99999999',
        value: lm,
      },
    },
    {
      type: 'select_multiple',
      name: 'status',
      required: false,
      label: translate('status'),
      help_text: translate('transferenceRequestFilterFragment.selectStatus'),
      attrs: {},
      options: StockTransferStatus.allStatus().map((status) => ({
        label: status.label,
        value: status.value,
      })),
      onChangeValue: (selectedOptions, setValueToField) => {
        const normalizedArray = Array.isArray(selectedOptions)
          ? selectedOptions
          : Object.values(selectedOptions)
    
        setValueToField('status', normalizedArray)
      },
    },
    {
      type: 'date_range',
      name: 'dtRange',
      label: translate('field.period.hint'),
      required: true,
    },
  ]

  return (
    <CommonReport
      initialFormData={restoredFilters}
      fluxReport
      title={translate('stock.transfer.report.title')}
      fields={fields}
      columns={columns}
      onSubmit={handleFetch}
      onExport={exportCsv}
      translate={translate}
      emptyReportMsg='no.data.found'
      order
      defaultOrder={'dtTransfer'}
      eyeIcon={true}
      idsList={ids || []}
      isStockTransfer
      user={user}
    />
  )
}

StockTransferReportContainer.propTypes = {
  auth: PropTypes.object.isRequired,
  count: PropTypes.number.isRequired,
  current: PropTypes.number.isRequired,
  error: PropTypes.object,
  fetch: PropTypes.func.isRequired,
  fetchFluxReport: PropTypes.func.isRequired,
  handleResize: PropTypes.func,
  list: PropTypes.array.isRequired,
  logout: PropTypes.func.isRequired,
  menu: PropTypes.object.isRequired,
  pages: PropTypes.number.isRequired,
  translate: PropTypes.func.isRequired,
  location: PropTypes.object,
}

/* istanbul ignore next */
const mapStateToProps = ({ stockTransferReportReducer, paginateReducer }) => ({
  ...stockTransferReportReducer,
  ...paginateReducer,
})

/* istanbul ignore next */
const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    { ...stockTransferReportActions, ...reportActions },
    dispatch
  )

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(
  translate(null, { wait: true, translateFuncName: 'translate' })(
    StockTransferReportContainer
  )
)

export { StockTransferReportContainer }
